import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { ChevronLeft, Loader2, Ellipsis, Info, Check, CircleAlert, X } from 'lucide-react';
import { TrashIcon } from '@/components/icons/trash-icon';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { DeleteDialog } from '@/components/shared/delete-dialog';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { MultiSelect } from '@/components/ui/multi-select';
import {
	useResponseStrategy,
	useCreateResponseStrategy,
	useUpdateResponseStrategy,
	useDeleteResponseStrategy,
	useGenerateResponseStrategy
} from '@/hooks/use-response-strategies';
import { usePermissions } from '@/hooks/use-permissions';
import { usePublishedCSCategories } from '@/hooks/use-cs-categories';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/sonner';

// Save feedback on this page uses the Figma "Approval Bar" snackbar, which
// doesn't match the app's default toast styling (solid success/error tint,
// bottom-center placement) — built as one-off custom toasts rather than
// changing the shared Toaster used everywhere else.
function showSaveResultToast(message: string) {
	toast.custom(
		(toastId) => (
			<div className='flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border border-gray-500 bg-green-100 backdrop-blur-[6px] w-[calc(100vw-2rem)] max-w-md'>
				<div className='flex items-center gap-3'>
					<Check size={16} className='text-green-500 shrink-0' />
					<p className='text-[13px] text-green-500'>{message}</p>
				</div>
				<button
					type='button'
					onClick={() => toast.dismiss(toastId)}
					className='flex items-center justify-center size-7 rounded-full text-gray-800 hover:bg-ink/5 shrink-0'
					aria-label='Dismiss'
				>
					<X size={14} />
				</button>
			</div>
		),
		{ position: 'bottom-center' }
	);
}

function showSaveErrorToast(message: string) {
	toast.custom(
		(toastId) => (
			<div className='flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border border-red-300 bg-red-100 backdrop-blur-[6px] w-[calc(100vw-2rem)] max-w-md'>
				<div className='flex items-center gap-3'>
					<CircleAlert size={16} className='text-red-500 shrink-0' />
					<p className='text-[13px] text-red-500'>{message}</p>
				</div>
				<button
					type='button'
					onClick={() => toast.dismiss(toastId)}
					className='flex items-center justify-center size-7 rounded-full text-gray-800 hover:bg-ink/5 shrink-0'
					aria-label='Dismiss'
				>
					<X size={14} />
				</button>
			</div>
		),
		{ position: 'bottom-center' }
	);
}

export default function ResponseStrategyForm() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const isUpdateMode = !!id;
	const { can } = usePermissions();
	const canEdit = can('edit', 'response-strategy');

	const [name, setName] = useState('');
	const [intents, setIntents] = useState<string[]>([]);
	const [merchantAsk, setMerchantAsk] = useState('');
	const [generatedStrategy, setGeneratedStrategy] = useState('');
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [showSaveEnableConfirm, setShowSaveEnableConfirm] = useState(false);
	const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

	const loadedIdRef = useRef<string | undefined>(undefined);
	const initialSnapshotRef = useRef({ name: '', intents: [] as string[], merchantAsk: '', generatedStrategy: '' });
	// Tracks the prompt text the current `generatedStrategy` was produced from, so we
	// only offer "Regenerate" once the merchant edits the prompt again — otherwise we
	// show the generated confirmation state instead of re-inviting a redundant generate.
	const lastGeneratedAskRef = useRef('');
	// The "✓ Generated Strategy" confirmation is momentary — shown right after a
	// successful generate, then cleared after a few seconds so the button area
	// goes quiet until the merchant edits the prompt again (which brings back
	// "Regenerate Strategy" via the lastGeneratedAskRef comparison below).
	const [justGenerated, setJustGenerated] = useState(false);
	const justGeneratedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		return () => {
			if (justGeneratedTimeoutRef.current) clearTimeout(justGeneratedTimeoutRef.current);
		};
	}, []);

	// Auto-size the title input to its actual text width (character count × ch
	// is a poor proxy for a proportional font) so the badge next to it sits
	// right against the visible text instead of floating past empty input space.
	const nameMeasureRef = useRef<HTMLSpanElement>(null);
	const [nameInputWidth, setNameInputWidth] = useState(0);
	useLayoutEffect(() => {
		if (nameMeasureRef.current) {
			setNameInputWidth(nameMeasureRef.current.offsetWidth);
		}
	}, [name]);

	const strategyQuery = useResponseStrategy(id || '');
	const strategy = isUpdateMode ? strategyQuery.data : undefined;
	const createMutation = useCreateResponseStrategy();
	const updateMutation = useUpdateResponseStrategy();
	const deleteMutation = useDeleteResponseStrategy();
	const generateMutation = useGenerateResponseStrategy();

	const { data: csCategories = [] } = usePublishedCSCategories();
	const intentOptions = useMemo(
		() => csCategories.map((cat) => ({ label: cat.name, value: cat.id })),
		[csCategories]
	);

	useEffect(() => {
		if (isUpdateMode && strategy && loadedIdRef.current !== strategy.id) {
			loadedIdRef.current = strategy.id;
			setName(strategy.name);
			setIntents(strategy.intents);
			setMerchantAsk(strategy.merchantAsk);
			setGeneratedStrategy(strategy.strategy);
			lastGeneratedAskRef.current = strategy.merchantAsk;
			initialSnapshotRef.current = {
				name: strategy.name,
				intents: strategy.intents,
				merchantAsk: strategy.merchantAsk,
				generatedStrategy: strategy.strategy
			};
		}
	}, [isUpdateMode, strategy]);

	const intentsChanged = (snapIntents: string[]) =>
		intents.length !== snapIntents.length || !intents.every((i) => snapIntents.includes(i));

	const hasUnsavedChanges = () => {
		const snap = initialSnapshotRef.current;
		if (name !== snap.name) return true;
		if (merchantAsk !== snap.merchantAsk) return true;
		if (generatedStrategy !== snap.generatedStrategy) return true;
		if (intentsChanged(snap.intents)) return true;
		return false;
	};

	// Draft-specific: "Save as Draft" only earns its place once the merchant
	// ask, intents, or the generated strategy text actually changed —
	// re-saving an untouched draft as a draft is a no-op the button
	// shouldn't invite.
	const hasDraftRelevantChanges = () => {
		const snap = initialSnapshotRef.current;
		if (merchantAsk !== snap.merchantAsk) return true;
		if (generatedStrategy !== snap.generatedStrategy) return true;
		if (intentsChanged(snap.intents)) return true;
		return false;
	};

	const handleBack = () => {
		if (hasUnsavedChanges()) {
			setShowDiscardConfirm(true);
		} else {
			navigate('/training/response-strategies');
		}
	};

	const handleGenerate = async () => {
		if (!merchantAsk.trim()) return;
		try {
			const result = await generateMutation.mutateAsync({ intents, merchantAsk: merchantAsk.trim() });
			setGeneratedStrategy(result.strategy);
			lastGeneratedAskRef.current = merchantAsk.trim();
			if (justGeneratedTimeoutRef.current) clearTimeout(justGeneratedTimeoutRef.current);
			setJustGenerated(true);
			justGeneratedTimeoutRef.current = setTimeout(() => setJustGenerated(false), 2500);
		} catch {
			toast.error('Failed to generate response strategy');
		}
	};

	const save = async (isActive: boolean) => {
		if (!name.trim()) {
			showSaveErrorToast('Name is required');
			return;
		}
		if (intents.length === 0) {
			showSaveErrorToast('At least one intent is required');
			return;
		}
		if (!generatedStrategy.trim()) {
			showSaveErrorToast('Generate a strategy before saving');
			return;
		}

		try {
			if (isUpdateMode && id) {
				await updateMutation.mutateAsync({
					id,
					data: {
						name: name.trim(),
						intents,
						merchantAsk: merchantAsk.trim(),
						strategy: generatedStrategy,
						isDraft: !isActive,
						isActive
					}
				});
				showSaveResultToast(isActive ? 'Response strategy saved' : 'Response strategy saved as draft');
				initialSnapshotRef.current = { name: name.trim(), intents, merchantAsk: merchantAsk.trim(), generatedStrategy };
				strategyQuery.refetch();
			} else {
				const created = await createMutation.mutateAsync({
					name: name.trim(),
					intents,
					merchantAsk: merchantAsk.trim(),
					strategy: generatedStrategy,
					isDraft: !isActive,
					isActive
				});
				showSaveResultToast(isActive ? 'Response strategy saved' : 'Response strategy saved as draft');
				navigate(`/training/response-strategies/${created.id}`);
			}
		} catch {
			showSaveErrorToast('Something went wrong');
		}
	};

	const handleSaveDraft = () => save(false);
	const handleSaveEnable = () => setShowSaveEnableConfirm(true);

	const handleDelete = async () => {
		if (!id) return;
		try {
			await deleteMutation.mutateAsync(id);
			toast.success('Response strategy deleted');
			navigate('/training/response-strategies');
		} catch {
			toast.error('Failed to delete response strategy');
		}
	};

	const isSaving = createMutation.isPending || updateMutation.isPending;
	const canSave = generatedStrategy.trim().length > 0 && canEdit;
	const isGenerateBlocked = !canEdit || !merchantAsk.trim();
	const hasEdits = hasUnsavedChanges();
	// A disabled item (not draft, not active — e.g. a template, or a strategy that was
	// paused) has no "back to draft" path from this form: only enabling makes sense —
	// unless its content is actively being edited, in which case saving as a draft
	// (rather than force-enabling it) needs to stay available.
	const isDisabled = isUpdateMode && strategy ? !strategy.isDraft && !strategy.isActive && !hasEdits : false;
	// An already-active, unedited strategy has nothing to save — hide the actions
	// entirely. They reappear as soon as the prompt or intents are edited.
	const hideActionButtons = isUpdateMode && strategy?.isActive === true && !hasEdits;
	// A template-derived override (generatedBy: "template:<id>") is this
	// account's link to the shared template, not a strategy of its own —
	// "Save as Draft" only applies to manually-created strategies. Disabling a
	// template override is done from the list's toggle, not this form.
	const isTemplateDerived = isUpdateMode && (strategy?.generatedBy?.startsWith('template:') ?? false);
	// On an existing draft, "Save as Draft" only makes sense once the merchant
	// ask or intents changed — otherwise it'd just re-save the same draft.
	const showSaveDraft =
		!isTemplateDerived && (!(isUpdateMode && strategy?.isDraft === true) || hasDraftRelevantChanges());
	// On a brand-new strategy, the save actions have nothing to save yet —
	// keep them hidden until the merchant has actually entered something.
	const hasStartedInput = isUpdateMode || name.trim().length > 0 || intents.length > 0 || merchantAsk.trim().length > 0;

	if (isUpdateMode && strategyQuery.isLoading) {
		return (
			<div className='flex flex-col h-full'>
				<div className='flex items-center gap-3 px-5 py-3 border-b border-gray-500'>
					<Skeleton className='h-8 w-8 rounded-md' />
					<Skeleton className='h-5 w-48' />
					<div className='flex items-center gap-2 ml-auto'>
						<Skeleton className='h-7 w-20 rounded-full' />
						<Skeleton className='h-7 w-28 rounded-full' />
					</div>
				</div>
				<div className='flex-1 p-6 space-y-4 max-w-[620px] w-full'>
					<Skeleton className='h-24 w-full rounded-2xl' />
					<Skeleton className='h-40 w-full rounded-2xl' />
				</div>
			</div>
		);
	}

	return (
		<div className='flex flex-col h-full'>
			{/* Header */}
			<div className='flex items-center gap-1 px-4 py-3.5 border-b border-gray-500'>
				<Button variant='ghost' size='icon' className='h-7 w-7' onClick={handleBack}>
					<ChevronLeft className='size-4 text-gray-800' />
				</Button>
				<div
					className={cn(
						'flex items-center gap-1 flex-1 min-w-0 pb-0.5',
						// The dashed underline is a "click to name this" affordance for a
						// brand-new, still-unsaved strategy — once it's saved, the name is
						// final and shown as plain text, not an editable-looking field.
						!isUpdateMode && 'border-b border-dashed border-ink-strong'
					)}
				>
					{/* Invisible mirror used only to measure the input's real rendered
					    text width (proportional fonts make ch-count sizing wrong). */}
					<span
						ref={nameMeasureRef}
						aria-hidden='true'
						className='absolute -z-10 opacity-0 pointer-events-none whitespace-pre text-base font-semibold'
					>
						{name || 'Untitled Strategy'}
					</span>
					<input
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder='Untitled Strategy'
						disabled={!canEdit || isUpdateMode}
						className='min-w-0 max-w-full border-none outline-none text-base font-semibold text-ink placeholder:text-gray-800 bg-transparent'
						style={{ width: nameInputWidth ? `${nameInputWidth + 2}px` : 'auto' }}
					/>

					{isUpdateMode && strategy?.isDraft && (
						<Badge variant='info' className='font-normal bg-blue-100 border-transparent shrink-0'>
							Draft
						</Badge>
					)}
				</div>

				<div className='flex items-center gap-1.5 shrink-0'>
					{hasStartedInput && !hideActionButtons && !isDisabled && showSaveDraft && (
						<Button variant='outline' size='sm' className='text-xs' onClick={handleSaveDraft} disabled={!canSave || isSaving}>
							Save as Draft
						</Button>
					)}
					<div className='flex items-center gap-3'>
						{hasStartedInput && !hideActionButtons && (
							<Button size='sm' className='text-xs' onClick={handleSaveEnable} disabled={!canSave || isSaving}>
								{isSaving ? 'Saving...' : 'Save and enable'}
							</Button>
						)}
						{hasStartedInput && !hideActionButtons && isUpdateMode && !isTemplateDerived && (
							<span className='h-4 w-px shrink-0 bg-gray-500' />
						)}
					</div>

					{/* Delete is the only item in this menu, and a template-derived override
					    can never be deleted (it's the account's live link to the shared
					    template — disabling it from the list is the only supported way to
					    turn it off) — hide the menu entirely rather than show it empty. */}
					{isUpdateMode && !isTemplateDerived && (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant='ghost' size='icon' className='h-7 w-7 text-gray-800'>
									<Ellipsis size={14} />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent
								align='end'
								className='bg-white/95 backdrop-blur-[6px] border-gray-300 shadow-[0px_3px_12px_0px_rgba(0,0,0,0.08)] rounded-2xl p-1.5'
							>
								<DropdownMenuItem
									onSelect={() => setDeleteOpen(true)}
									className='rounded-[10px] pl-2.5 pr-1.5 py-1 text-[13px] text-red-500 focus:text-red-500'
								>
									<TrashIcon className='size-4 text-current' />
									Delete
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					)}
				</div>
			</div>

			{/* Content */}
			<div className='flex-1 overflow-y-auto'>
				<div className='flex flex-col gap-7 max-w-[620px] w-full p-5'>
					{/* Choose when to use the strategy */}
					<div className='flex flex-col gap-3'>
						<p className='text-[13px] font-medium text-gray-800'>Choose when to use the strategy</p>
						<div className='border border-gray-500 rounded-2xl p-3 flex flex-col gap-2'>
							<p className='text-xs font-medium text-gray-900'>Select intents</p>
							<MultiSelect
								options={intentOptions}
								defaultValue={intents}
								onValueChange={setIntents}
								disabled={!canEdit}
								showAllSelected
								placeholder='Select'
								popoverPlaceholder='Search intents...'
								hideSelectAll
								className='text-xs border-gray-500'
							/>
						</div>
					</div>

					{/* Response strategy */}
					<div className='flex flex-col gap-3'>
						<p className='text-[13px] font-medium text-gray-800'>Response strategy</p>
						<div className='border border-gray-500 rounded-[20px] p-3 flex flex-col gap-4'>
							<div className='flex flex-col gap-2'>
								<div className='flex items-center gap-1'>
									<span className='text-xs font-medium text-gray-900'>Prompt</span>
									<TooltipProvider>
										<Tooltip>
											<TooltipTrigger asChild>
												<Info size={14} className='text-gray-800 cursor-default' />
											</TooltipTrigger>
											<TooltipContent side='top' align='start' alignOffset={-36} variant='dark' className='max-w-72'>
											 {isTemplateDerived
												? "This came from a template. You can customize when it should apply by editing its intents, but the response instructions themselves can't be changed."
												: 'Refine your prompt as many times as you like. When you’re ready, generate a response strategy and test it.'}
											</TooltipContent>
										</Tooltip>
									</TooltipProvider>
								</div>
								<div className='relative'>
									<Textarea
										value={merchantAsk}
										onChange={(e) => setMerchantAsk(e.target.value)}
										placeholder='Type something...'
										readOnly={isTemplateDerived}
										disabled={!canEdit}
										className={
											isTemplateDerived
												? 'min-h-[101px] max-h-[400px] rounded-[20px] placeholder:text-xs resize-none bg-gray-300 text-gray-700 cursor-default focus-visible:ring-0 border-gray-500'
												: 'min-h-[101px] max-h-[400px] rounded-[20px] placeholder:text-xs pb-12 resize-none border-gray-500'
										}
									/>
									{!isTemplateDerived &&
										(() => {
											const promptUnchangedSinceGeneration =
												!!generatedStrategy && merchantAsk.trim() === lastGeneratedAskRef.current;
											if (!generateMutation.isPending && promptUnchangedSinceGeneration && !justGenerated) {
												// Confirmation already shown and timed out — quiet until the prompt is edited.
												return null;
											}
											return (
												<div className='absolute bottom-3 right-3'>
													{!generateMutation.isPending && promptUnchangedSinceGeneration && justGenerated ? (
														<span className='flex items-center gap-1.5 text-xs font-medium text-gray-800'>
															<Check size={14} className='text-green-600' />
															Generated Strategy
														</span>
													) : (
														<Button
															variant='outline'
															size='sm'
															className={cn(
																'text-xs gap-1.5 rounded-full disabled:opacity-100',
																isGenerateBlocked &&
																	'bg-gray-300 border-transparent text-gray-700 hover:bg-gray-300'
															)}
															onClick={handleGenerate}
															disabled={isGenerateBlocked || generateMutation.isPending}
														>
															{generateMutation.isPending ? (
																<>
																	<Loader2 size={14} className='animate-spin' />
																	Generating Strategy...
																</>
															) : generatedStrategy ? (
																'Re-generate Strategy'
															) : (
																'Generate Strategy'
															)}
														</Button>
													)}
												</div>
											);
										})()}
								</div>
							</div>

							{generatedStrategy && (
								<>
									<div
										className='h-px w-full'
										style={{
											backgroundImage:
												'repeating-linear-gradient(to right, var(--color-gray-400) 0, var(--color-gray-400) 5px, transparent 5px, transparent 10px)'
										}}
									/>
									<div className='flex flex-col gap-3 pb-1'>
										<div className='flex items-center gap-2'>
											<img src='/assets/persona/ai-sparkle.svg' alt='' className='size-8 shrink-0' />
											<p className='text-sm font-medium text-foreground'>Generated Response Strategy</p>
										</div>
										<p className='text-[13px] text-foreground whitespace-pre-wrap'>{generatedStrategy}</p>
										<p className='text-xs italic text-gray-800'>
											The application's limitations shape this response, meaning some prompt aspects may be bypassed.
										</p>
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			</div>

			<DeleteDialog
				open={deleteOpen}
				onOpenChange={setDeleteOpen}
				onConfirm={handleDelete}
				title='Delete strategy?'
				description='Deleting this response strategy is permanent. Do you want to continue?'
				isLoading={deleteMutation.isPending}
			/>

			<AlertDialog open={showSaveEnableConfirm} onOpenChange={setShowSaveEnableConfirm}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Save and enable this strategy?</AlertDialogTitle>
						<AlertDialogDescription>Kim will start using it immediately for matching conversations.</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								setShowSaveEnableConfirm(false);
								save(true);
							}}
						>
							Save and enable
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<DeleteDialog
				open={showDiscardConfirm}
				onOpenChange={setShowDiscardConfirm}
				onConfirm={() => {
					setShowDiscardConfirm(false);
					navigate('/training/response-strategies');
				}}
				title='Discard changes?'
				description="These changes haven't been saved. If you discard them, the live strategy will remain unchanged and your edits will be lost."
				confirmLabel='Discard'
			/>
		</div>
	);
}
